import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import { PDFGenerationOverlay } from '../components/PDFGenerationOverlay';
import { honorairesAPI, assurancesAPI } from '../services/api';
import SmartPagination from '../components/SmartPagination';
import { Plus, Eye, Pencil, Trash2, FileDown, RefreshCw, Search, CheckCircle } from 'lucide-react';
import { playSound } from '../utils/SoundPlayer';
import toast from 'react-hot-toast';



const STATUS_MAP = {
    brouillon: { label: 'Brouillon', cls: 'badge-gray' },
    emis: { label: 'Émis', cls: 'badge-blue' },
    paye: { label: 'Payé', cls: 'badge-green' },
    annule: { label: 'Annulé', cls: 'badge-red' },
};

const HonorairesPage = () => {
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statut, setStatut] = useState('');
    const [assurance, setAssurance] = useState('');
    const [code, setCode] = useState('');
    const [ville, setVille] = useState('');
    const [assurancesData, setAssurancesData] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({ open: false, id: null, num: null, loading: false });
    const [pdfStatus, setPdfStatus] = useState('idle');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const fetchData = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await honorairesAPI.list({ page, limit: 20, search, statut, startDate, endDate, assurance, code, ville });
            setList(res.data.data || []);
            setPagination(res.data.pagination || { total: 0, page: 1, pages: 1 });
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, [search, statut, startDate, endDate, assurance, code, ville]);

    useEffect(() => {
        const loadAssurances = async () => {
            try {
                const res = await assurancesAPI.list();
                setAssurancesData(res.data.data || []);
            } catch (err) { console.error('Error loading assurances', err); }
        };
        loadAssurances();
    }, []);

    useEffect(() => { fetchData(1); }, [fetchData]);

    const handlePDF = async (id, num) => {
        try {
            setPdfStatus('generating');
            const res = await honorairesAPI.generatePDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Honoraire_${num}.pdf`; a.click();
            URL.revokeObjectURL(url);

            setPdfStatus('success');
            playSound('pdf');
            setTimeout(() => setPdfStatus('idle'), 1500);
        } catch {

            setPdfStatus('idle');
            toast.error('Erreur PDF', { id: 'pdf' });
        }
    };

    const handleExportEtat = async () => {
        if (!startDate || !endDate) {
            toast.error("Veuillez sélectionner une période (Date début et fin)");
            return;
        }
        try {
            setPdfStatus('generating');
            const res = await honorairesAPI.exportEtat({
                start_date: startDate,
                end_date: endDate,
                payed: statut === 'paye' ? 'payed' : (statut === 'emis' ? 'impayed' : 'all'),
                assurance: assurance,
                code: code,
                ville: ville
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Etat_Honoraires_${new Date().toISOString().split('T')[0]}.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            setPdfStatus('success');
            playSound('pdf');
            setTimeout(() => setPdfStatus('idle'), 1500);
        } catch (error) {

            setPdfStatus('idle');
            toast.error("Erreur lors de l'exportation");
        }
    };

    const handleDeleteRequest = (id, num) => {
        setConfirmModal({ open: true, id, num, loading: false });
    };

    const handleDeleteConfirm = async () => {
        setConfirmModal(m => ({ ...m, loading: true }));
        try {
            playSound('delete');
            await honorairesAPI.delete(confirmModal.id);
            toast.success("Note d'honoraire supprimée");
            setConfirmModal({ open: false, id: null, num: null, loading: false });
            fetchData(pagination.page);
        } catch {
            toast.error('Erreur lors de la suppression');
            setConfirmModal(m => ({ ...m, loading: false }));
        }
    };


    const handleMarkAsPaid = async (id) => {
        try {
            await honorairesAPI.markAsPaid(id);
            toast.success("Marqué como payé");
            fetchData(pagination.page);
        } catch {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const formatCurrency = (v) => v
        ? new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' DA'
        : '—';

    return (
        <Layout title="Notes d'Honoraires">
            <PDFGenerationOverlay status={pdfStatus} />
            {/* Confirm Delete Modal */}
            <ConfirmModal
                open={confirmModal.open}
                title="Supprimer la note d'honoraire"
                message={`Voulez-vous vraiment supprimer la note d'honoraire ${confirmModal.num} ? Cette action est irréversible.`}
                confirmLabel="Oui, supprimer"
                cancelLabel="Annuler"
                variant="danger"
                loading={confirmModal.loading}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmModal({ open: false, id: null, num: null, loading: false })}
            />

            <div className="flex-between mb-4">
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-800)' }}>Gestion des Honoraires</h2>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>{pagination.total} note(s) au total</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-pdf" onClick={handleExportEtat}>
                        <FileDown size={16} /> État des Paiements (PDF)
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate('/honoraires/nouvelle')}>
                        <Plus size={16} /> Nouvelle Note
                    </button>
                </div>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div className="search-box">
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Période:</span>
                            <input
                                type="date"
                                className="form-control"
                                style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                            />
                            <span style={{ color: 'var(--text-muted)' }}>à</span>
                            <input
                                type="date"
                                className="form-control"
                                style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                            />
                        </div>

                        <select
                            className="form-control"
                            style={{ width: 'auto', padding: '8px 12px' }}
                            value={statut}
                            onChange={e => setStatut(e.target.value)}
                        >
                            <option value="">Tous les statuts</option>
                            {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>

                        <select
                            className="form-control"
                            style={{ width: '180px', padding: '8px 12px' }}
                            value={assurance}
                            onChange={e => { setAssurance(e.target.value); setCode(''); setVille(''); }}
                        >
                            <option value="">Assurance (Toutes)</option>
                            {[...new Set(assurancesData.map(a => a.name))].sort().map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>

                        {assurance && (
                            <>
                                <select
                                    className="form-control"
                                    style={{ width: '120px', padding: '8px 12px' }}
                                    value={code}
                                    onChange={e => setCode(e.target.value)}
                                >
                                    <option value="">Code (Tous)</option>
                                    {[...new Set(assurancesData.filter(a => a.name === assurance).map(a => a.code).filter(Boolean))].sort().map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>

                                <select
                                    className="form-control"
                                    style={{ width: '180px', padding: '8px 12px' }}
                                    value={ville}
                                    onChange={e => setVille(e.target.value)}
                                >
                                    <option value="">Ville (Toutes)</option>
                                    {[...new Set(assurancesData.filter(a => a.name === assurance).map(a => a.city).filter(Boolean))].sort().map(v => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => fetchData(pagination.page)}><RefreshCw size={14} /></button>
                </div>

                {loading ? <div className="loading-spinner"><div className="spinner" /></div> :
                    list.length === 0 ? (
                        <div className="empty-state">
                            <p>💰</p>
                            <h3>Aucune note d'honoraire</h3>
                            <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => navigate('/honoraires/nouvelle')}>
                                <Plus size={14} /> Nouvelle Note
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table>
                                <thead>
                                    <tr>
                                        <th>N° Note</th>
                                        <th>N° Rapport</th>
                                        <th>Assuré</th>
                                        <th>Assurance</th>
                                        <th>Montant HT</th>
                                        <th>TVA</th>
                                        <th>Montant TTC</th>
                                        <th>Date</th>
                                        <th>Statut</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(list) && list.map(h => (
                                        <tr key={h.id}>
                                            <td style={{ fontWeight: '700', color: 'var(--gold-500)', fontFamily: 'monospace', fontSize: '12px' }}>{h.numero_honoraire || '—'}</td>
                                            <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{h.numero_rapport || '—'}</td>
                                            <td style={{ fontWeight: '600' }}>
                                                {h.nom_assure || '—'}
                                                {(h.immatriculation || h.marque_vehicule || h.modele_vehicule) && (
                                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '400', marginTop: '2px' }}>
                                                        {h.marque_vehicule || ''} {h.modele_vehicule || ''} {h.immatriculation || ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{h.nom_assurance || '—'}</td>
                                            <td>{formatCurrency(h.montant_ht)}</td>
                                            <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{h.tva_pct ? h.tva_pct + '%' : '—'}</td>
                                            <td>
                                                <span style={{ fontWeight: '700', color: 'var(--success)' }}>{formatCurrency(h.montant_ttc)}</span>
                                            </td>
                                            <td style={{ fontSize: '12px' }}>{h.date_emission ? new Date(h.date_emission).toLocaleDateString('fr-DZ') : '—'}</td>
                                            <td>
                                                <span className={`badge ${STATUS_MAP[h.statut]?.cls || 'badge-gray'}`}>
                                                    {STATUS_MAP[h.statut]?.label || h.statut || '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="table-actions">
                                                    <button className="btn btn-ghost btn-sm action-view" title="Voir" onClick={() => navigate(`/honoraires/${h.id}`)}>
                                                        <Eye size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-edit" title="Modifier" onClick={() => navigate(`/honoraires/${h.id}/modifier`)}>
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-pdf" title="Générer PDF" onClick={() => handlePDF(h.id, h.numero_honoraire)}>
                                                        <FileDown size={18} />
                                                    </button>
                                                    {h.statut !== 'paye' && (
                                                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--success)' }} title="Marquer como Payé" onClick={() => handleMarkAsPaid(h.id)}>
                                                            <CheckCircle size={18} />
                                                        </button>
                                                    )}
                                                    <button className="btn btn-ghost btn-sm action-delete" title="Supprimer" onClick={() => handleDeleteRequest(h.id, h.numero_honoraire)}>
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                {/* Smart Pagination */}
                <SmartPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit || 20}
                    onPageChange={(p) => fetchData(p)}
                />
            </div>
        </Layout>
    );
};

export default HonorairesPage;
